// K1 · Desktop v3: her room (D_Desk.dc.html, design record 电脑端-v3-2026-11-03). The window is her room: her loop
// fills it, her words are subtitles on the right, the next card is a paper card on her desk, Today is the cards
// spread out on the desk, Us / Me is a journal on her desk, a streak / perfect day / level-up is a title in the room.
// Same data and rules as the phone: the subtitles are the phone chat's own items (state.messages + the live stream),
// Stuck runs in that chat (stuck-chat), a card ticked here leaves its note there. Pages the room doesn't draw (a
// chapter, share, VIP, crisis, the buddy page, switch, friends, the week, the collection, Me's test tools) open as
// the phone screen in a panel over the dimmed room (app.tsx passes it as `panel`); Esc or ✕ goes back to the room.
import type { ComponentChildren } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { AppState, Card, ChatErrorCode, CharId, LaterWhen, Message, PlanData, ProposedCard, Reward, RouteStop, StuckAction, StuckChatData, StuckReason, SummaryData } from '@shared/types';
import { C } from '@shared/content/characters';
import { LATER_CHIPS, LINES, STUCK_REASONS } from '@shared/content/lines';
import { CHAPTER, SOUNDS } from '@shared/content/story';
import { STUCK_CARD, stuckStep } from '@shared/content/stuck';
import { dateLabel, isBigReward, localMinutes, taskShort, weekdayName, fmtTime } from '@shared/rules';
import {
  useApp, appStore, sendChat, sendPhoto, clearChatFlags, confirmPlan, editPlan, editCard, startCard, removeCard, laterCard,
  undoChange, acceptNudge, answerOffer, goodnight, rewardSeen, toast, dismissToast, updateSettings, switchBuddy, signOut, errorText, restoreCard,
  doneCard, undoneCard, stuckChat, stuckAction,
} from '../../store';
import { back, go, replace, useParams, useScreen } from '../../router';
import { Screen, EmbeddedScreen, Icon, ToastHost } from '../../components';
import { useSize } from '../../lib/useSize';
import { say } from '../../lib/say';
import { timerMinutes, track } from '../../lib/analytics';
import type { ActionFrom, AppEventData } from '../../api/types';
import { setBetaQuiet } from '../../feedback/Beta';
import { desktop, onNative } from '../../native';
import { micError } from '../onboarding/Questions';
import { derive, readPhoto, type Outgoing, type VoiceOut } from '../day/ChatScreen';
import { dayOf, serverNow, timeOf } from '../day/util';
import type { Clip } from '../../audio/recorder';
import { RoomMedia, type ReactKind, type RoomReact } from './RoomMedia';
import { RoomTalk, toLines, type Choice, type Line } from './RoomTalk';
import { RoomDesk, type DeskAct, type DeskStamp, type DeskView } from './RoomDesk';
import { RoomSpread, RoomEdit, RoomPlanTime, type EditValue, type SpreadCard } from './RoomSpread';
import { RoomTitle, type TitleBeat } from './RoomTitle';
import { RoomJournal, type DeskPrefs } from './RoomJournal';
import { TimeZoneSheet } from '../me/TimeZoneSheet';
import { roomFit } from './roomFit';
import { STOPPED_KEY, deskSession, deskTimeAt, readStopped, stop as stopStored, unstop } from '../../lib/deskSession';
import { CHOICE, DESK, NOTE, QUIET_MS, ROOM, SOUND, SPLIT_HELP, SPREAD, STAMP_MS, TITLE, TITLE_MS, TOAST, TOP } from './roomCopy';
import s from './room.module.css';

type View = null | 'today' | 'us' | 'me';
type EditState = null | { kind: 'card'; id: string; v: EditValue } | { kind: 'draft'; i: number; v: EditValue } | { kind: 'plantime' };
interface Cele { beats: TitleBeat[]; i: number; first: boolean; t: number }

const t = (x: string, v: Record<string, string | number>) => x.replace(/\{(\w+)\}/g, (m, k) => (v[k] != null ? String(v[k]) : m));
const lastOf = (ms: Message[], f: (m: Message) => boolean) => { for (let i = ms.length - 1; i >= 0; i--) if (f(ms[i])) return ms[i]; return null; };
const SOUNDS_KEY = 'xm.room.sounds';
// Per-viewer conveniences on this computer (browser storage may be blocked: then they simply don't stick).
const readList = (k: string): string[] => { try { const v = JSON.parse(localStorage.getItem(k) || '[]'); return Array.isArray(v) ? v.filter((x) => typeof x === 'string').slice(-40) : []; } catch { return []; } };
const saveList = (k: string, v: string[]) => { try { localStorage.setItem(k, JSON.stringify(v.slice(-40))); } catch { /* storage blocked */ } };
const short = (x: string) => taskShort(x);
/** Admin event: working together on her desk ended (Done, Stuck, ✕), with the minutes on its timer. */
const sessionEnd = (cd: Card, how: AppEventData['cowork_end']['how']) =>
  track('cowork_end', { card: cd.id, minutes: timerMinutes(cd.startedAt, serverNow()), how });

/** A line only this window says (D_Desk's moveLater: 'Moved “…” to after lunch'), not stored: after the line it followed. */
interface RoomNote { key: string; day: string; after: string | null; text: string }
function withNotes(base: Line[], notes: RoomNote[]): Line[] {
  if (!notes.length) return base;
  const out = base.slice();
  for (const n of notes) {
    const line: Line = { k: 'note', key: n.key, text: n.text, icon: 'arrowRight' };
    if (n.after == null) { out.unshift(line); continue; }
    const i = out.findIndex((l) => l.key === n.after);
    if (i < 0) continue; // the line it followed is gone (another day, a message replaced): so is the note
    let j = i + 1;
    while (j < out.length && out[j].key.startsWith('moved:')) j++;
    out.splice(j, 0, line);
  }
  return out;
}

/** Routes the room answers itself (the rest of app.tsx's desktop routes are panels over it). */
export const ROOM_ROUTES = ['K1', 'C', 'T', 'W', 'S', 'R', 'U1'] as const;

export function Room({ panel }: { panel?: ComponentChildren | null }) {
  const st = useApp((x) => x.state);
  if (!st) return <Screen theme="margo" id="K1" />;
  return <RoomView st={st} panel={panel || null} />;
}

function RoomView({ st, panel }: { st: AppState; panel: ComponentChildren | null }) {
  const chat = useApp((x) => x.chat);
  const storeToast = useApp((x) => x.toast);
  const screen = useScreen();
  const params = useParams();
  const ch = st.ch, c = C[ch], R = ROOM[ch], L = LINES[ch];
  const root = useRef<HTMLDivElement>(null);
  const size = useSize(root, { w: 1280, h: 800 });
  const fit = roomFit(size.w, size.h, ch);
  const input = useRef<HTMLInputElement>(null);
  const photoIn = useRef<HTMLInputElement>(null);

  // ---------------------------------------------------------------- local room state
  const [view, setView] = useState<View>(null);
  const [edit, setEdit] = useState<EditState>(null);
  const [lift, setLift] = useState(false);
  const [only, setOnly] = useState(false);
  const [soundOpen, setSoundOpen] = useState(false);
  const [quiet, setQuiet] = useState(false);
  const [react, setReact] = useState<RoomReact | null>(null);
  const [stamp, setStamp] = useState<DeskStamp | null>(null);
  const [cele, setCele] = useState<Cele | null>(null);
  const [lastTick, setLastTick] = useState<string | null>(null);
  const [doneBusy, setDoneBusy] = useState<Card | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [stopped, setStopped] = useState<string[]>(readStopped);
  const [prefs, setPrefs] = useState<DeskPrefs | null>(null);
  const [tzOpen, setTzOpen] = useState(false);
  const [notes, setNotes] = useState<RoomNote[]>([]);
  const lastLine = useRef<string | null>(null);
  const [, setTick] = useState(0);
  // Chat glue (as in the phone chat): what was just sent, a voice message on its way, "didn't catch any words".
  const [draft, setDraft] = useState('');
  const [outgoing, setOutgoing] = useState<Outgoing | null>(null);
  const [voice, setVoice] = useState<VoiceOut | null>(null);
  const [heard, setHeard] = useState<string | null>(null);
  const [shortDay, setShortDay] = useState(false);
  /** Her stuck help is on its way (the model may take a few seconds for "too big" / "where to start"): she types. */
  const [helpWait, setHelpWait] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const glance = (kind: ReactKind = 'glance') => setReact({ kind, t: Date.now() });
  // Day or night on the user's clock (the server's, debug days included): checked on every state and every 15 s.
  const time = deskTimeAt(serverNow(), st.user.tz);
  useEffect(() => { const iv = setInterval(() => setTick((x) => x + 1), 15000); return () => clearInterval(iv); }, []);

  // ---------------------------------------------------------------- the day
  const tz = st.user.tz;
  const ids = new Set(st.messages.map((m) => m.id));
  const msgs = st.messages.concat(chat.pending.filter((m) => !ids.has(m.id)));
  const todays = msgs.filter((m) => dayOf(m.ts, tz) === st.today);
  const pendingPlan = lastOf(todays, (m) => m.kind === 'plan' && (m.data as PlanData | undefined)?.status === 'pending');
  const draftCards: ProposedCard[] = (pendingPlan?.data as PlanData | undefined)?.cards || [];
  const live = st.plan.cards.filter((x) => x.st !== 'removed' && x.st !== 'moved').slice().sort((a, b) => a.at - b.at || a.order - b.order);
  const todo = live.filter((x) => x.st === 'todo');
  const doneN = live.filter((x) => x.st === 'done').length;
  const moved = st.plan.cards.filter((x) => x.st === 'moved');
  const next = todo[0] || null;
  // The card being worked on: the same rule as the small window and the menu-bar timer (lib/deskSession).
  const session = deskSession(st, stopped);
  const stuckMsg = lastOf(msgs, (m) => m.kind === 'stuck' && (m.data as StuckChatData | undefined)?.status === 'open');
  const sData = stuckMsg ? (stuckMsg.data as StuckChatData) : null;
  const sCard = sData ? st.plan.cards.find((x) => x.id === sData.cardId && x.st === 'todo') || null : null;
  const stuck = stuckMsg && sData && sCard ? { m: stuckMsg, d: sData, card: sCard } : null;
  const labelOf = (x: { id: string; time: string }) => { const n = live.findIndex((y) => y.id === x.id); return `${R.unit} ${Math.max(1, n + 1)} · ${x.time}`; };

  // ---------------------------------------------------------------- chat
  const derived = useMemo(() => derive(st, chat, outgoing, voice, heard, params.chat === 'evening', shortDay, helpWait), [st, chat, outgoing, voice, heard, params.chat, shortDay, helpWait]);
  const { mode, offer, saidGoodnight } = derived;
  useEffect(() => { if (!chat.sending) setOutgoing(null); }, [chat.sending]);
  const focusInput = () => setTimeout(() => input.current?.focus(), 30);
  const send = async (text: string) => {
    const x = text.trim();
    if (!x || chat.sending) return;
    setDraft(''); setOutgoing({ kind: 'text', text: x }); setVoice(null); setHeard(null);
    await sendChat({ text: x });
  };
  const sendVoice = async (clip: Clip) => {
    setOutgoing(null); setHeard(null); setVoice({ clip, failed: false });
    const got: { landed?: boolean; err?: { code?: ChatErrorCode; message: string } } = {};
    const end = await sendChat({ audio: { b64: clip.b64, mime: clip.mime } }, (e) => {
      if (e.t === 'user' || e.t === 'limit' || e.t === 'crisis') got.landed = true;
      else if (e.t === 'error' && !got.landed) got.err = { code: e.code, message: e.message };
    }, { quiet: true });
    if (got.landed || end === 'aborted') { setVoice(null); return; }
    if (got.err?.code === 'no_words') { setVoice(null); setHeard(got.err.message); return; }
    if (got.err?.code === 'region') { setVoice(null); toast(got.err.message); return; }
    if (!alive.current) { toast("Your voice message didn't go through. Try again."); return; }
    setVoice({ clip, failed: true });
  };
  const retryVoice = () => { if (voice?.failed && !chat.sending) void sendVoice(voice.clip); };
  const onPhoto = async (e: Event) => {
    const el = e.currentTarget as HTMLInputElement;
    const file = el.files?.[0];
    el.value = '';
    if (!file) return;
    try {
      const img = await readPhoto(file);
      setOutgoing({ kind: 'photo', thumb: img.thumb || null }); setVoice(null); setHeard(null);
      await sendPhoto(img);
    } catch (err) { toast(errorText(err)); }
  };

  // Free limit -> VIP (the unsent text back in the input). Crisis -> the crisis card. A card finished by talking
  // (complete_card) -> the same celebration as Done. Stream errors -> toast.
  useEffect(() => {
    if (chat.limit) {
      const lim = lastOf(chat.pending, (m) => m.kind === 'limit') || (!chat.sending ? lastOf(st.messages, (m) => m.kind === 'limit') : null);
      if (!lim && chat.sending) return;
      const text = (lim?.data as { text?: string } | undefined)?.text;
      if (typeof text === 'string' && text) setDraft(text);
      clearChatFlags();
      go('M4');
    } else if (chat.crisis) {
      clearChatFlags();
      go('M6');
    } else if (chat.rewardFor && !chat.sending) {
      const rw = appStore.get().state?.reward || null;
      clearChatFlags();
      if (rw) celebrate(rw, st.plan.cards.find((x) => x.id === rw.id) || null, false);
    } else if (chat.error && !chat.sending) {
      toast(chat.error);
      clearChatFlags();
    }
  }, [chat.limit, chat.crisis, chat.rewardFor, chat.error, chat.sending, chat.pending]);

  // ---------------------------------------------------------------- actions
  const closeAll = () => { setView(null); setEdit(null); setSoundOpen(false); setOnly(false); setLift(false); setTzOpen(false); };
  const planToday = () => { closeAll(); glance(); focusInput(); };
  // `from` (admin events): where Start / Stuck was clicked: the card on her desk ('next'), today spread out ('today'),
  // working together on it ('cowork'), her offer in the subtitles ('chat').
  const startTogether = async (id: string, alt: string | null = null, from?: ActionFrom) => {
    closeAll();
    const again = stopped.includes(id);
    if (again) { unstop(id); setStopped(readStopped()); }
    glance();
    const cd = st.plan.cards.find((x) => x.id === id);
    // Started before and still going (the phone, the small window): just show it. Stopped with ✕: start it again, as
    // the card itself (her smaller step from Stuck ended with the ✕) unless a step is asked for.
    if (!cd?.startedAt || alt || again) await startCard(id, again ? alt : alt ?? cd?.alt ?? null, from);
  };
  const stopSession = (cd: Card) => { sessionEnd(cd, 'close'); stopStored(cd.id); setStopped(readStopped()); };
  // The small window (another window of the app) reads and may write the same list.
  useEffect(() => {
    const on = (e: StorageEvent) => { if (e.key === STOPPED_KEY) setStopped(readStopped()); };
    window.addEventListener('storage', on);
    return () => window.removeEventListener('storage', on);
  }, []);
  // Her ask shows at once (the store's optimistic copy, id tmp_…) and the server's arrives a moment later. An action on
  // the ask (Never mind) waits for the real id, so a quick click never hits a message the server hasn't written yet.
  const askWait = useRef<Promise<string | null> | null>(null);
  const asked = (p: Promise<{ messageId?: string } | null>) => { askWait.current = p.then((r) => r?.messageId || null); return p; };
  const onStuck = async (id: string, a: StuckAction) => {
    const real = id.startsWith('tmp_') && askWait.current ? (await askWait.current) || id : id;
    return a.action === 'again' ? asked(stuckAction(real, a)) : stuckAction(real, a);
  };
  const stuckOn = async (id: string, from?: ActionFrom) => { closeAll(); glance(); await asked(stuckChat(id, null, from)); };
  const unlockLabel = (stop: RouteStop | null) => {
    if (!stop || !stop.ready || stop.type === 'season') return TITLE.nextSeason;
    if (stop.type === 'chap' && stop.chap) return `Chapter ${stop.chap}, ${CHAPTER[ch][stop.chap - 1]}`;
    if (stop.type === 'place') return `a new look for ${c.placeName}`;
    return stop.label;
  };
  const beatsOf = (rw: Reward): TitleBeat[] => {
    const out: TitleBeat[] = [];
    if (rw.streakTo != null) out.push({ kind: 'streak', big: `${rw.streakTo}-day streak`, sub: say(L.streak, { k: rw.streakTo, n: c.name }) });
    if (rw.perfect) out.push({ kind: 'perfect', big: 'Perfect day', sub: say(L.perfect, { n: c.name }) + TITLE.perfectPts });
    if (rw.levelTo > rw.levelFrom) out.push({ kind: 'level', big: `Level ${rw.levelTo}`, sub: say(L.levelUp, { n: c.name }) + t(TITLE.levelNew, { x: unlockLabel(rw.unlock) }) });
    return out;
  };
  function celebrate(rw: Reward, card: Card | null, fromSpread: boolean) {
    const big = isBigReward(rw);
    glance('nod');
    const text = rw.streakTo != null ? `DAY ${rw.streakTo}` : rw.perfect ? 'PERFECT' : rw.levelTo > rw.levelFrom ? `LV ${rw.levelTo}` : 'DONE';
    if (!fromSpread || big) setStamp({ key: Date.now(), label: card ? labelOf(card) : '', title: short(rw.title), text, pts: rw.gained });
    if (fromSpread && big) setView(null);
    // A big one is a title over the room: no toast from before it ("+10 points · Undo", "4 cards on your desk") stays on
    // top of it (and toasts wait while it plays, see the render).
    if (big) { dismissToast(); setCele({ beats: beatsOf(rw), i: 0, first: true, t: Date.now() }); setLastTick(null); }
    else if (card) {
      setLastTick(card.id);
      toast(t(TOAST.done, { t: short(card.title), k: rw.gained }), { undo: () => void undo(card.id) });
    }
    void rewardSeen();
  }
  // The phone's tick() rules (screens/day/cards.tsx): only a card still to do; big -> her title in the room (the phone's
  // R), else the light celebration: stamp, "+N", rewardSeen, a toast with Undo, her done note in the chat.
  const finish = async (card: Card, how: 'tick' | 'work') => {
    if (doneBusy || card.st !== 'todo') return;
    const fromSpread = view === 'today';
    // The paper keeps what it showed while it is stamped: her smaller step when working together on it, else the card.
    setDoneBusy(how === 'work' && card.alt ? { ...card, title: card.alt } : card); setLift(false);
    const rw = await doneCard(card.id, { from: how === 'work' ? 'work-chat' : 'chat' });
    setDoneBusy(null);
    if (rw && how === 'work') sessionEnd(card, 'done');
    if (rw) celebrate(rw, card, fromSpread);
  };
  const undo = async (id: string) => { dismissToast(); setLastTick(null); setStamp(null); await undoneCard(id); };
  const confirm = async () => {
    if (!pendingPlan || busy) return;
    setBusy('confirm');
    const before = new Set(st.plan.cards.map((x) => x.id));
    const r = await confirmPlan(pendingPlan.id);
    setBusy(null);
    if (!r) return;
    setView(null); setEdit(null); glance('nod');
    const k = r.state.plan.cards.filter((x) => !before.has(x.id) && x.st === 'todo').length || r.state.plan.cards.filter((x) => x.st === 'todo').length;
    toast(k === 1 ? TOAST.onDeskOne : t(TOAST.onDesk, { k }));
  };
  const changeSomething = () => { setView('today'); toast(t(TOAST.changeHint, { n: c.name })); };
  const later = async (id: string, when: LaterWhen) => {
    const cd = st.plan.cards.find((x) => x.id === id);
    const r = await laterCard(id, when, 'today'); // only today spread out has Later
    if (!r || !cd) return;
    const x = when === 'In 1 hour' ? 'an hour from now' : when.toLowerCase();
    toast(t(TOAST.later, { t: short(cd.title), x }));
    setNotes((ns) => ns.concat([{ key: `moved:${cd.id}:${Date.now()}`, day: st.today, after: lastLine.current, text: t(NOTE.retimed, { t: short(cd.title), x }) }]).slice(-8));
  };
  // D_Desk: "Removed: {short title}" with Undo.
  const remove = async (cd: Card) => {
    const r = await removeCard(cd.id, { toast: false, from: 'today' }); // today spread out (a card, or its edit sheet)
    if (r) toast(t(TOAST.removed, { t: short(cd.title) }), { undo: () => { dismissToast(); void restoreCard(cd.id); } });
  };
  const saveDraft = (cards: ProposedCard[]) => pendingPlan && editPlan(pendingPlan.id, cards.map((x) => ({ time: x.time, title: x.title, min: x.min })));

  // ---------------------------------------------------------------- choices under her line (D_Desk `choices`)
  const choice = (label: string, run: () => void, strong = false, muted = false): Choice => ({ label, run, strong, muted, busy: busy === label });
  let choices: Choice[] = [];
  const lastKinds = st.messages.slice(-2).map((m) => m.kind);
  const lastBuddy = lastOf(msgs, (m) => m.role !== 'user');
  if (lastKinds.includes('crisis')) choices = [];
  else if (stuck) {
    const { m, d, card } = stuck;
    if (d.step === 'ask') {
      const pick = async (reason: StuckReason) => { setHelpWait(true); try { await stuckChat(card.id, reason); } finally { setHelpWait(false); } };
      choices = helpWait ? [] : STUCK_REASONS.map((r) => choice(r.label, () => void pick(r.id as StuckReason)))
        .concat([choice(CHOICE.neverMind, () => void onStuck(m.id, { action: 'close' }), false, true)]);
    } else if (d.reason === 'time') {
      choices = LATER_CHIPS.map((w) => choice(w, () => void onStuck(m.id, { action: 'later', when: w })))
        .concat([choice(CHOICE.somethingElse, () => void onStuck(m.id, { action: 'again' }), false, true)]);
    } else {
      const h = helpOf(stuck);
      choices = [choice(h.btn, h.go, true), choice(CHOICE.somethingElse, () => void onStuck(m.id, { action: 'again' }), false, true)];
    }
  } else if (lastBuddy?.kind === 'limit') choices = [choice(CHOICE.vip, () => go('M4'), true)];
  else if (mode === 'n3' && offer) {
    choices = [
      choice(CHOICE.offerYes, async () => { const r = await answerOffer(offer.id, true); if (r?.cardId) void startTogether(r.cardId, null, 'chat'); }, true),
      choice(CHOICE.offerNo, () => void answerOffer(offer.id, false)),
    ];
  } else if (mode === 'proposed' && pendingPlan) choices = [choice(CHOICE.looksGood, () => void confirm(), true), choice(CHOICE.change, changeSomething)];
  else if (lastBuddy?.kind === 'nudge' && (lastBuddy.data as { status?: string } | undefined)?.status === 'pending') {
    const nm = lastBuddy;
    choices = [choice(CHOICE.accept, async () => { setBusy(CHOICE.accept); await acceptNudge(nm.id); setBusy(null); glance('nod'); }, true)];
  } else if (mode === 'evening') choices = saidGoodnight ? [] : [choice(CHOICE.goodnight, async () => { setBusy(CHOICE.goodnight); await goodnight(); setBusy(null); }, true)];
  else if (mode === 'n2') choices = [choice(CHOICE.shortDay, () => { setShortDay(true); planToday(); }, true)];
  else if (mode === 'ask') choices = [choice(CHOICE.myDay, planToday, true), choice(CHOICE.photo, () => photoIn.current?.click())];
  if (chat.sending) choices = [];

  // ---------------------------------------------------------------- her help on the card (stuck, step 'help')
  function helpOf(x: NonNullable<typeof stuck>) {
    const h = x.d.help || null;
    const reason = (x.d.reason || h?.reason || 'start') as StuckReason;
    const go1 = (action: 'start' | 'split') => async () => {
      if (action === 'start' && stopped.includes(x.card.id)) { unstop(x.card.id); setStopped(readStopped()); }
      const r = await onStuck(x.m.id, { action });
      if (r && action === 'start') glance();
    };
    if (h && h.reason === 'big' && h.split && h.split.length) {
      const k = h.split.length;
      return { head: t(SPLIT_HELP.head, { k }), title: h.split[0].title, foot: t(SPLIT_HELP.foot, { rest: h.split.slice(1).map((y) => y.title).join(', ') }), btn: t(SPLIT_HELP.btn, { k }), go: go1('split') };
    }
    const step = (h && stuckStep(h, x.card.title)) || short(x.card.title);
    return { head: STUCK_CARD[reason].head, title: step.charAt(0).toUpperCase() + step.slice(1), foot: t(DESK.helpFoot, { n: c.name }), btn: STUCK_CARD[reason].btn || STUCK_CARD.distract.btn, go: go1('start') };
  }

  // ---------------------------------------------------------------- the card on her desk
  const act = (key: string, label: string, run: () => void, o: Partial<DeskAct> = {}): DeskAct => ({ key, label, run, ...o });
  let desk: DeskView;
  if (doneBusy) {
    desk = { kind: 'next', label: labelOf(doneBusy), title: doneBusy.title, foot: t(DESK.nextFoot, { m: doneBusy.min, d: doneN, t: live.length }), aria: doneBusy.title, stack: Math.max(0, todo.length - 1), posed: true, actions: [], actionsOnHover: false };
  } else if (stuck && stuck.d.step === 'help' && stuck.d.reason !== 'time') {
    const h = helpOf(stuck);
    desk = { kind: 'help', label: h.head, title: h.title, foot: h.foot, aria: h.title, open: h.go, stack: Math.max(0, todo.length - 1), posed: true, actionsOnHover: false,
      actions: [act('go', h.btn, h.go, { icon: 'play', strong: true, aria: `${h.btn}: ${h.title}` }), act('else', CHOICE.somethingElse, () => void onStuck(stuck.m.id, { action: 'again' }))] };
  } else if (stuck) {
    desk = { kind: 'stuck', label: t(stuck.d.reason === 'time' ? DESK.whenLabel : DESK.stuckLabel, { time: stuck.card.time }), title: stuck.card.title, foot: `${stuck.card.min} min`, aria: `Stuck on ${stuck.card.title}`, stack: Math.max(0, todo.length - 1), posed: true, actions: [], actionsOnHover: false };
  } else if (session) {
    const title = session.alt || session.title;
    desk = { kind: 'sess', label: DESK.sessLabel, title, foot: t(session.min ? DESK.sessFoot : DESK.sessFootBare, { m: session.min, n: c.name }), aria: `Working together: ${title}`, stack: Math.max(0, todo.length - 1), posed: true, actionsOnHover: false,
      actions: [
        act('done', 'Done', () => void finish(session, 'work'), { icon: 'check', strong: true, aria: `Done: ${title}` }),
        act('stuck', 'Stuck', () => { sessionEnd(session, 'stuck'); void stuckOn(session.id, 'cowork'); }, { aria: `Stuck on: ${title}` }),
        act('stop', '', () => stopSession(session), { icon: 'close', aria: DESK.stop }),
      ] };
  } else if (pendingPlan && draftCards.length) {
    desk = { kind: 'draft', label: t(DESK.draftLabel, { k: draftCards.length }), title: draftCards[0].title, foot: t(DESK.draftFoot, { n: c.name }), aria: 'See today’s draft', open: () => setView('today'), stack: draftCards.length - 1, posed: false, actionsOnHover: true,
      actions: [act('ok', CHOICE.looksGood, () => void confirm(), { strong: true, busy: busy === 'confirm' }), act('all', `See all ${draftCards.length}`, () => setView('today'))] };
  } else if (!live.length) {
    desk = { kind: 'blank', label: t(DESK.blankLabel, { day: weekdayName(st.today).toUpperCase() }), title: DESK.blankTitle, foot: t(DESK.blankFoot, { n: c.name }), aria: `Plan today with ${c.name}`, open: planToday, stack: 0, posed: false, actionsOnHover: true,
      actions: [act('plan', `Plan with ${c.name}`, planToday, { strong: true })] };
  } else if (next) {
    // The card's own title: her smaller step (alt) belongs to working together on it, and ends with the ✕.
    desk = { kind: 'next', label: labelOf(next), title: next.title, foot: t(DESK.nextFoot, { m: next.min, d: doneN, t: live.length }), aria: `Next card: ${next.title}. Click to see today`, open: () => setView('today'), stack: todo.length - 1, posed: false, actionsOnHover: true,
      actions: [
        act('start', 'Start together', () => void startTogether(next.id, null, 'next'), { icon: 'play', strong: true, aria: `Start together: ${next.title}` }),
        act('done', 'Done', () => void finish(next, 'tick'), { icon: 'check', aria: `Done: ${next.title}` }),
        act('stuck', 'Stuck', () => void stuckOn(next.id, 'next'), { aria: `Stuck on: ${next.title}` }),
      ] };
  } else {
    desk = { kind: 'alldone', label: t(DESK.doneLabel, { d: doneN, t: live.length }), title: DESK.doneTitle, foot: t(DESK.doneFoot, { k: st.streak.count }), stamp: 'DONE', aria: 'All done today. Click to see the cards', open: () => setView('today'), stack: 0, posed: false, actions: [], actionsOnHover: false };
  }
  const sessFrom = session && !doneBusy && desk.kind === 'sess' && session.startedAt ? Date.parse(session.startedAt) : null;

  // ---------------------------------------------------------------- today, spread out
  const draftMode = !!pendingPlan && draftCards.length > 0;
  const spreadCards: SpreadCard[] = draftMode
    ? draftCards.map((x, i) => ({ id: 'd' + i, label: `${R.unit} ${i + 1} · ${x.time}`, title: x.title, foot: `${x.min} min`, done: false, pencil: true }))
    : live.map((x) => ({ id: x.id, label: labelOf(x), title: x.title, foot: x.st === 'done' ? t(SPREAD.doneAt, { time: timeOf(x.doneAt, tz) || x.time }) : `${x.min} min${next && x.id === next.id ? SPREAD.upNext : ''}`, done: x.st === 'done', pencil: false }));
  const spreadActions = (sc: SpreadCard, laterOpen: boolean, setLater: (on: boolean) => void): DeskAct[] => {
    if (draftMode) {
      const i = Number(sc.id.slice(1));
      const out = [act('change', 'Change', () => openEdit(sc), { strong: true, aria: `Change: ${sc.title}` })];
      if (draftCards.length > 1) out.push(act('off', '', () => void saveDraft(draftCards.filter((_, j) => j !== i)), { icon: 'close', aria: `Take it off the plan: ${sc.title}` }));
      return out;
    }
    const cd = st.plan.cards.find((x) => x.id === sc.id);
    if (!cd) return [];
    if (cd.st === 'done') return lastTick === cd.id ? [act('undo', 'Undo', () => void undo(cd.id), { aria: `Undo: ${cd.title}` })] : [];
    if (laterOpen) return LATER_CHIPS.map((w) => act(w, w, () => { setLater(false); void later(cd.id, w); })).concat([act('back', 'Back', () => setLater(false))]);
    return [
      act('start', 'Start', () => void startTogether(cd.id, null, 'today'), { icon: 'play', strong: true, aria: `Start together: ${cd.title}` }),
      act('done', 'Done', () => void finish(cd, 'tick'), { icon: 'check', aria: `Done: ${cd.title}` }),
      act('stuck', 'Stuck', () => void stuckOn(cd.id, 'today'), { aria: `Stuck on: ${cd.title}` }),
      act('edit', 'Edit', () => openEdit(sc), { aria: `Edit: ${cd.title}` }),
      act('later', 'Later', () => setLater(true), { aria: `Later: ${cd.title}` }),
      act('remove', '', () => void remove(cd), { icon: 'close', aria: `Remove: ${cd.title}` }),
    ];
  };
  function openEdit(sc: SpreadCard) {
    if (draftMode) {
      const i = Number(sc.id.slice(1));
      const x = draftCards[i];
      if (x) setEdit({ kind: 'draft', i, v: { t: x.title, tm: x.time, mn: x.min } });
      return;
    }
    const cd = st.plan.cards.find((x) => x.id === sc.id);
    if (cd && cd.st === 'todo') setEdit({ kind: 'card', id: cd.id, v: { t: cd.title, tm: cd.time, mn: cd.min } });
  }
  const saveEdit = async (v: EditValue) => {
    const e = edit;
    setEdit(null);
    if (!e || e.kind === 'plantime') return;
    if (e.kind === 'draft') { await saveDraft(draftCards.map((x, j) => (j === e.i ? { ...x, title: v.t, time: v.tm, min: v.mn } : x))); return; }
    const cd = st.plan.cards.find((x) => x.id === e.id);
    if (!cd) return;
    const patch: { title?: string; time?: string; min?: number } = {};
    if (v.t !== cd.title) patch.title = v.t;
    if (v.tm !== cd.time) patch.time = v.tm;
    if (v.mn !== cd.min) patch.min = v.mn;
    if (Object.keys(patch).length) await editCard(cd.id, patch);
  };
  const removeEdit = async () => {
    const e = edit;
    setEdit(null);
    if (!e || e.kind === 'plantime') return;
    if (e.kind === 'draft') await saveDraft(draftCards.filter((_, j) => j !== e.i));
    else { const cd = st.plan.cards.find((x) => x.id === e.id); if (cd) await remove(cd); }
  };

  // ---------------------------------------------------------------- subtitles
  const planTime = st.tomorrowPlanTime || st.user.planTime;
  const todayMark = st.streak.week.find((d) => d.day === st.today)?.mark;
  const streakFoot = (data: SummaryData, onToday: boolean) => {
    const final = data.mark && data.mark !== 'today' ? data.mark : null;
    const liveDay = onToday && !final;
    const mark = final || (liveDay ? todayMark : data.done > 0 ? 'lit' : null);
    const n = liveDay ? st.streak.count : data.streak;
    if (mark === 'lit') return n ? `Streak: ${n} ${n === 1 ? 'day' : 'days'}` : 'Day lit';
    if (mark === 'rest') return 'Streak safe: rest day';
    if (mark === 'freeze') return 'Streak safe: weekly freeze';
    if (liveDay && st.streak.count > 0 && st.streak.freezeLeft > 0) return 'Streak safe: weekly freeze';
    return null;
  };
  const base = toLines(derived.items, st, {
    undoChange: (id) => void undoChange(id),
    openWeek: () => go('U3'),
    openUnlock: (stop) => (stop?.type === 'chap' && stop.chap ? go('U2', { chap: stop.chap }) : setView('us')),
    openPlanTime: () => setEdit({ kind: 'plantime' }),
    openCrisis: () => go('M6'),
    planTime,
    streakFoot,
  });
  lastLine.current = base.length ? base[base.length - 1].key : null;
  const lines = withNotes(base, notes.filter((n) => n.day === st.today));

  // ---------------------------------------------------------------- glance / nod at the moments the design lists
  const planSeen = useRef<string | null>(pendingPlan?.id || null);
  useEffect(() => { if (pendingPlan && planSeen.current !== pendingPlan.id) { planSeen.current = pendingPlan.id; glance(); } }, [pendingPlan?.id]);
  // Back to the room after a while in another app: she looks up.
  useEffect(() => {
    let away = 0;
    const blur = () => { away = Date.now(); };
    const focus = () => { if (away && Date.now() - away > 30000) glance(); away = 0; };
    window.addEventListener('blur', blur); window.addEventListener('focus', focus);
    return () => { window.removeEventListener('blur', blur); window.removeEventListener('focus', focus); };
  }, []);

  // ---------------------------------------------------------------- stamp, title, quiet, keys
  useEffect(() => { if (!stamp) return undefined; const x = setTimeout(() => setStamp(null), STAMP_MS); return () => clearTimeout(x); }, [stamp?.key]);
  const nextBeat = () => setCele((cc) => (cc && cc.i < cc.beats.length - 1 ? { ...cc, i: cc.i + 1, first: false, t: Date.now() } : null));
  useEffect(() => { if (!cele) return undefined; const x = setTimeout(nextBeat, TITLE_MS + (cele.first && cele.i === 0 ? 700 : 0)); return () => clearTimeout(x); }, [cele?.t]);
  const busyUi = !!(view || edit || soundOpen || cele || lift || panel || tzOpen);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let last = 0;
    const arm = () => { if (timer) clearTimeout(timer); timer = setTimeout(() => setQuiet(true), QUIET_MS); };
    const poke = () => { const n = Date.now(); if (n - last < 250) return; last = n; setQuiet(false); arm(); };
    arm();
    const ev = ['mousemove', 'pointerdown', 'keydown', 'wheel'] as const;
    ev.forEach((e) => window.addEventListener(e, poke, { passive: true }));
    return () => { if (timer) clearTimeout(timer); ev.forEach((e) => window.removeEventListener(e, poke)); };
  }, []);
  const isQuiet = quiet && !busyUi;
  useEffect(() => { setBetaQuiet(only); return () => setBetaQuiet(false); }, [only]);
  // Esc closes what's on top (the panel handles its own). Not while the mic is listening: Esc cancels that. The handler
  // reads the latest state through a ref, so two quick presses close two layers.
  const esc = useRef<() => boolean>(() => false);
  esc.current = () => {
    if (panel || tzOpen || document.querySelector('[data-live="true"]')) return false; // the time-zone sheet handles its own
    if (edit) setEdit(null);
    else if (cele) nextBeat();
    else if (view) setView(null);
    else if (soundOpen) setSoundOpen(false);
    else if (only) setOnly(false);
    else return false;
    return true;
  };
  useEffect(() => {
    const on = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented && esc.current()) e.preventDefault(); };
    document.addEventListener('keydown', on);
    return () => document.removeEventListener('keydown', on);
  }, []);
  // ⌥ Space / Alt+Space (desktop shell) and the tray "Talk": the window comes forward with the cursor in the input.
  useEffect(() => { const on = () => { closeAll(); focusInput(); }; window.addEventListener('xm:talk', on); return () => window.removeEventListener('xm:talk', on); }, []);
  // A 0.1.0 desktop shell has no small window of its own (its bridge has no getPrefs): while working together, its old
  // mini window stands in when the user leaves the room, and closes when they come back. Newer shells do this natively.
  const sessRef = useRef(session);
  sessRef.current = session;
  useEffect(() => {
    const d = (window as unknown as { xmDesktop?: { getPrefs?: unknown } }).xmDesktop;
    if (!desktop.available() || desktop.isMini() || typeof d?.getPrefs === 'function') return undefined;
    let open = false;
    const blur = () => { if (sessRef.current && !open) { open = true; desktop.openMini(); } };
    const focus = () => { if (open) { open = false; desktop.closeMini(); } };
    const off = onNative((e) => { if (e.type === 'mini-closed') open = false; });
    window.addEventListener('blur', blur); window.addEventListener('focus', focus);
    return () => { off(); window.removeEventListener('blur', blur); window.removeEventListener('focus', focus); };
  }, []);
  // Routes that land in the room (notifications, tray, old links): T = today spread out, W:<id> = work on it together,
  // S = stuck on it, U1 = the journal, C = talk. Then the address is the room again.
  useEffect(() => {
    if (screen === 'K1') return;
    // The address goes back to the room first: a new screen clears the toast (store.ts), so a toast the route itself
    // brings (a light Done from R) comes after it.
    if ((['C', 'T', 'W', 'S', 'R', 'U1'] as string[]).includes(screen)) replace('K1');
    if (screen === 'T') setView('today');
    else if (screen === 'U1') setView('us');
    else if (screen === 'C') { setView(null); focusInput(); }
    else if (screen === 'W' && params.cur) { const cd = st.plan.cards.find((x) => x.id === params.cur && x.st === 'todo'); if (cd) void startTogether(cd.id, params.alt || null, 'cowork'); }
    else if (screen === 'S' && params.cur) { const cd = st.plan.cards.find((x) => x.id === params.cur && x.st === 'todo'); if (cd) void stuckOn(cd.id); }
    // R:<id> (the small window's "See it" after a big Done): the in-room celebration for that reward, then it is seen.
    else if (screen === 'R' && st.reward) celebrate(st.reward, st.plan.cards.find((x) => x.id === st.reward!.id) || null, false);
  }, [screen]);
  // "On this computer" lives in the desktop shell (stay on screen, notice, step aside for calls).
  useEffect(() => {
    const d = (window as unknown as { xmDesktop?: { getPrefs?: () => Promise<DeskPrefs>; onEvent?: (cb: (e: { type: string; prefs?: DeskPrefs }) => void) => () => void } }).xmDesktop;
    if (!d?.getPrefs) return undefined;
    void d.getPrefs().then((p) => setPrefs(p || {})).catch(() => setPrefs(null));
    const off = d.onEvent?.((e) => { if (e.type === 'prefs' && e.prefs) setPrefs(e.prefs); });
    return () => { try { off?.(); } catch { /* ignore */ } };
  }, []);
  const setPref = (key: string, on: boolean) => {
    const d = (window as unknown as { xmDesktop?: { setPrefs?: (p: DeskPrefs) => unknown } }).xmDesktop;
    setPrefs((p) => ({ ...(p || {}), [key]: on }));
    try {
      const r = d?.setPrefs?.({ [key]: on });
      void Promise.resolve(r).then((p) => { if (p && typeof p === 'object') setPrefs(p as DeskPrefs); }).catch(() => undefined);
    } catch { /* ignore */ }
  };

  // ---------------------------------------------------------------- sounds (the ones her route has given you)
  const soundStops = st.buddy.route.filter((r) => r.type === 'sound');
  const [soundsOn, setSoundsOn] = useState<string[]>(() => readList(SOUNDS_KEY));
  useRoomSounds(soundsOn.filter((id) => st.buddy.sounds.includes(id) && SOUNDS[id]));
  const toggleSound = (id: string) => { const nx = soundsOn.includes(id) ? soundsOn.filter((x) => x !== id) : soundsOn.concat([id]); setSoundsOn(nx); saveList(SOUNDS_KEY, nx); };

  // ---------------------------------------------------------------- render
  const nav: { v: Exclude<View, null>; label: string }[] = [{ v: 'today', label: 'TODAY' }, { v: 'us', label: 'US' }, { v: 'me', label: 'ME' }];
  const toggleView = (v: Exclude<View, null>) => { setView(view === v ? null : v); setSoundOpen(false); setOnly(false); setLift(false); };
  const ui = { '--u': String(fit.u) } as Record<string, string>;
  const uiBox = { width: fit.vw, height: fit.vh, transform: `scale(${fit.u})` };
  const chromeOp = isQuiet ? 0 : 1;
  const journalActions = {
    chapter: (n: number) => go('U2', { chap: n }),
    locked: (x: string) => toast(x),
    share: () => go('SH', { mode: 'week', from: 'K1' }),
    switchTo: async (k: CharId) => {
      const prev = ch;
      const r = await switchBuddy(k);
      if (r) toast(t(TOAST.switched, { n: C[k].name }), { undo: () => { dismissToast(); void switchBuddy(prev); } });
    },
    vip: () => go('M4'),
    memory: () => go('M2'),
    planTime: () => setEdit({ kind: 'plantime' }),
    checkin: () => { const order = ['light', 'steady', 'close'] as const; const i = order.indexOf(st.user.checkin); void updateSettings({ checkin: order[(i + 1) % 3] }); },
    tz: () => setTzOpen(true),
    pref: setPref,
    crisis: () => go('M6'),
    signOut: () => void signOut(),
    tools: () => go('M1'),
  };

  return (
    <Screen theme={ch} id="K1" label={`Xiaoman · ${c.name}'s room`} bg="#0B0B0E">
      <div ref={root} class={s.room} data-time={time} data-only={only ? 'true' : 'false'} data-quiet={isQuiet ? 'true' : 'false'} style={{ background: c.bg }}>
        <RoomMedia ch={ch} time={time} react={react} fit={fit} playing />
        <div class={s.shade} role="img" aria-label={R.alt} />
        <div class={s.scrim} aria-hidden="true" style={{ width: 700 * fit.u, opacity: only ? 0 : isQuiet ? 0.82 : 1 }} />

        {!only && <RoomDesk ch={ch} fit={fit} night={time === 'night'} view={desk} lift={lift} setLift={setLift} stamp={stamp} sessFrom={sessFrom} />}

        <div class={s.ui} style={{ ...ui, ...uiBox }}>
          {!only && (
            <>
              <div class={s.who} style={{ opacity: chromeOp }}>
                <button type="button" class={s.whoBtn} aria-label={t(TOP.buddyAria, { n: c.name })} onClick={() => toggleView('me')}>
                  <span class={s.whoName}>{c.name.toUpperCase()}</span><span class={s.ai}>AI</span>
                </button>
                <RoomClock where={R.where} tz={tz} />
              </div>
              <nav class={s.nav} aria-label="Xiaoman" style={{ opacity: chromeOp }}>
                <button type="button" class={s.streak} aria-label={t(TOP.streakAria, { k: st.streak.count }) + (st.streak.litToday ? TOP.streakLit : '')} onClick={() => toggleView('us')}>
                  <Icon name="flame" size={18} strokeWidth={1.8} color={st.streak.litToday ? c.acc : '#E9E4DA'} fill={st.streak.litToday ? c.acc : 'none'} />
                  <span>{st.streak.count}</span>
                </button>
                <span class={s.sep} aria-hidden="true" />
                {nav.map((n) => (
                  <button key={n.v} type="button" class={s.navBtn} aria-pressed={view === n.v ? 'true' : 'false'} onClick={() => toggleView(n.v)}>{n.label}</button>
                ))}
                <span class={s.sep} aria-hidden="true" />
                <button type="button" class={s.iconBtn} aria-label={TOP.sound} aria-expanded={soundOpen ? 'true' : 'false'} data-on={soundOpen ? 'true' : 'false'} onClick={() => { setSoundOpen(!soundOpen); setView(null); }}>
                  <Icon name="music" size={18} strokeWidth={1.9} />
                </button>
                <button type="button" class={s.iconBtn} aria-label={t(TOP.only, { n: c.name })} onClick={() => { setOnly(true); setView(null); setSoundOpen(false); setLift(false); }}>
                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" style={{ fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round', strokeLinejoin: 'round' }}><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>
                </button>
              </nav>
              <RoomTalk
                st={st} lines={lines} choices={choices} quiet={isQuiet}
                draft={draft} setDraft={setDraft} onSend={() => void send(draft)} onAudio={(clip) => { void sendVoice(clip); }}
                onMicError={(e) => { micError(e, 'You can type instead.'); input.current?.focus(); }}
                onToast={(x) => toast(x)} onRetryVoice={retryVoice} onCrisis={() => go('M6')}
                sending={chat.sending} inputRef={input}
              />
            </>
          )}
          {only && <button type="button" class={s.showAll} style={{ opacity: isQuiet ? 0 : 1 }} onClick={() => setOnly(false)}>{TOP.showAll}</button>}

          {view === 'today' && !only && (
            <RoomSpread
              ch={ch} vw={fit.vw}
              title={draftMode ? SPREAD.draftTitle : SPREAD.title}
              sub={draftMode ? t(SPREAD.draftSub, { date: dateLabel(st.today), k: draftCards.length, n: c.name }) : t(SPREAD.sub, { date: dateLabel(st.today), d: doneN, t: live.length })}
              foot={draftMode ? SPREAD.draftFoot : moved.length ? t(SPREAD.movedFoot, { list: moved.map((x) => short(x.title)).join(', '), motto: R.motto }) : R.motto}
              cards={spreadCards} actions={spreadActions} open={openEdit}
              confirm={draftMode ? () => void confirm() : null} confirmBusy={busy === 'confirm'}
              close={() => setView(null)}
            />
          )}
          {edit && edit.kind !== 'plantime' && (
            <RoomEdit
              key={edit.kind === 'card' ? edit.id : 'd' + edit.i} ch={ch} vw={fit.vw} value={edit.v} draft={edit.kind === 'draft'}
              canRemove={edit.kind === 'card' || draftCards.length > 1}
              onSave={(v) => void saveEdit(v)} onRemove={() => void removeEdit()} onClose={() => setEdit(null)}
            />
          )}
          {edit && edit.kind === 'plantime' && (
            <RoomPlanTime ch={ch} name={c.name} vw={fit.vw} value={st.user.planTime} onClose={() => setEdit(null)}
              onDone={(v) => { setEdit(null); if (v !== st.user.planTime) void updateSettings({ planTime: v }); }} />
          )}
          {(view === 'us' || view === 'me') && !only && (
            <RoomJournal st={st} view={view} setView={(v) => setView(v)} close={() => setView(null)} vw={fit.vw} vh={fit.vh} time={time} prefs={prefs} a={journalActions} />
          )}
          {cele && !only && <RoomTitle ch={ch} vw={fit.vw} beats={cele.beats} i={cele.i} week={st.streak.week} today={st.today} first={cele.first} onNext={nextBeat} />}
          {tzOpen && (
            <div class={s.tzHost}>
              <TimeZoneSheet open tz={st.user.tz} auto={st.user.tzAuto} onClose={() => setTzOpen(false)} />
            </div>
          )}
          {soundOpen && !only && (
            <div class={s.soundPop} role="dialog" aria-label={TOP.sound}>
              <p class={s.soundHead}>{SOUND.head}</p>
              {soundStops.map((r) => {
                const has = r.reached && !!r.sound && !!SOUNDS[r.sound];
                const on = has && soundsOn.includes(r.sound!);
                const sub = r.reached ? t(SOUND.unlocked, { k: r.level }) : r.ready ? t(SOUND.locks, { k: r.level }) : SOUND.soon;
                return (
                  <button key={r.idx} type="button" class={s.soundRow} role="switch" aria-checked={on ? 'true' : 'false'} aria-disabled={has ? undefined : 'true'} data-has={has ? 'true' : 'false'} onClick={() => { if (has) toggleSound(r.sound!); }}>
                    <span class={s.soundText}><span class={s.soundLabel}>{r.label}</span><span class={s.soundSub}>{sub}</span></span>
                    <span class={s.switchTrack} data-on={on ? 'true' : 'false'} aria-hidden="true"><span /></span>
                  </button>
                );
              })}
              <p class={s.soundFoot}>{t(SOUND.foot, { p: c.poss })}</p>
            </div>
          )}
          {storeToast && !panel && !cele && (
            <div key={storeToast.id} class={s.toast}>
              <div role="status" aria-live="polite" class={s.toastPill}>
                <span>{storeToast.text}</span>
                {storeToast.undo && <button type="button" class={s.toastUndo} onClick={storeToast.undo}>Undo</button>}
                {!storeToast.undo && storeToast.action && <button type="button" class={s.toastAct} onClick={() => { dismissToast(); storeToast.action!.run(); }}>{storeToast.action.label}</button>}
              </div>
            </div>
          )}
        </div>

        <input ref={photoIn} type="file" accept="image/*" class="xm-sr-only" tabIndex={-1} aria-hidden="true" onChange={(e) => void onPhoto(e)} />
        {panel && <RoomPanel name={c.name}>{panel}</RoomPanel>}
      </div>
    </Screen>
  );
}

/** "The theater · Tuesday, 10:26 AM" (her place line under her name), on the user's clock. */
function RoomClock({ where, tz }: { where: string; tz: string }) {
  const [, setTick] = useState(0);
  useEffect(() => { const iv = setInterval(() => setTick((x) => x + 1), 15000); return () => clearInterval(iv); }, []);
  const now = serverNow();
  const day = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'long' }).format(new Date(now));
  return <span class={s.where}>{`${where} · ${day}, ${fmtTime(localMinutes(now, tz))}`}</span>;
}

/** A phone screen in a centered panel over the dimmed room (pages the room doesn't draw). Esc or ✕: back. */
function RoomPanel({ name, children }: { name: string; children: ComponentChildren }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (ref.current?.querySelector('[role="dialog"][aria-modal="true"]')) return; // a sheet inside handles it
      e.preventDefault();
      back('K1');
    };
    document.addEventListener('keydown', on);
    return () => document.removeEventListener('keydown', on);
  }, []);
  return (
    <div class={s.panelLayer}>
      <button type="button" class={s.panelScrim} aria-hidden="true" tabIndex={-1} onClick={() => back('K1')} />
      <div ref={ref} class={s.panelWrap} role="dialog" aria-modal="true" aria-label={`Over ${name}'s room`}>
        <div class={s.panel}>
          <EmbeddedScreen.Provider value>{children}</EmbeddedScreen.Provider>
          <ToastHost />
        </div>
        <button type="button" class={s.panelX} aria-label={`Back to ${name}'s room`} onClick={() => back('K1')}>
          <Icon name="close" size={18} strokeWidth={2.4} />
        </button>
      </div>
    </div>
  );
}

/** Loop the chosen sounds (route rewards) quietly while the room is open. */
function useRoomSounds(ids: string[]) {
  const els = useRef<Record<string, HTMLAudioElement>>({});
  const key = ids.slice().sort().join(',');
  useEffect(() => {
    for (const id of ids) {
      const file = SOUNDS[id]?.file;
      if (!file) continue;
      let a = els.current[id];
      if (!a) { a = new Audio(file); a.loop = true; a.volume = 0.45; els.current[id] = a; }
      void a.play().catch(() => { /* needs a click first; the next toggle plays it */ });
    }
    for (const [id, a] of Object.entries(els.current)) if (!ids.includes(id)) a.pause();
  }, [key]);
  useEffect(() => () => { for (const a of Object.values(els.current)) a.pause(); }, []);
}

